import {
	ActivityListResponse,
	GroceryListResponse,
	HouseholdMembersResponse,
	MealPlanListResponse,
	RecipeListResponse,
	TodoListResponse,
} from '@hewey/shared'
import { z } from 'zod'
import {
	clearStore,
	META,
	openDb,
	readAll,
	readOne,
	remove,
	RESOURCES,
	write,
} from './db.ts'

// Which resources survive a page load, and the schema each one is parsed with
// on the way back in — reading off disk is a boundary like any other, so a
// cache left by an older app version fails the parse and falls through to the
// network instead of becoming a ghost bug.
//
// Keyed by the first segment of the resource key, so two call sites sharing a
// key can't disagree about whether it persists. Absent means memory-only:
// `['export-job']` is an ephemeral progress poller, and a stale one restored
// from disk would be actively wrong.
const SCHEMAS: Record<string, z.ZodType> = {
	activities: ActivityListResponse,
	recipes: RecipeListResponse,
	todos: TodoListResponse,
	groceries: GroceryListResponse,
	'household-members': HouseholdMembersResponse,
	'meal-plan': MealPlanListResponse,
	'meals-widget': MealPlanListResponse,
}

export const schemaFor = (
	key: ReadonlyArray<unknown>,
): z.ZodType | undefined =>
	typeof key[0] === 'string' ? SCHEMAS[key[0]] : undefined

// Bump to discard every stored entry — how a shape change is handled instead
// of migrations.
const CACHE_VERSION = 1

// Nothing is refetched more than a few times a week in normal use, so anything
// older than this is a key that stopped being asked for. `meal-plan` is keyed
// by date window and accumulates a new one as time passes; this is what keeps
// that bounded.
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

// Safari has been known to leave indexedDB.open hanging until the next user
// gesture. Boot waits on hydration, so a hang would be a white screen — start
// without the cache instead.
const OPEN_TIMEOUT_MS = 1500

const Meta = z.object({
	version: z.number().int(),
	userId: z.string().nullable(),
})

type Stored = { data: unknown; updatedAt: number }

// Raw values read at boot, held until the hook that owns the key asks for one
// and parses it. Emptied as it's consumed.
const hydrated = new Map<string, unknown>()

// What each key was last written as. Activities, Todo and Meals poll every ten
// seconds; without this the cache would spend a disk write per tick storing
// bytes it already has.
const written = new Map<string, string>()

let available = false
let boundUserId: string | null = null

export const boundUser = (): string | null => boundUserId

const reset = async (userId: string | null) => {
	hydrated.clear()
	written.clear()
	boundUserId = userId
	if (!available) return
	await clearStore(RESOURCES)
	if (userId === null) await clearStore(META)
	else await write(META, 'cache', { version: CACHE_VERSION, userId })
}

const load = async () => {
	const cutoff = Date.now() - MAX_AGE_MS
	const stale: string[] = []
	for (const [key, value] of await readAll(RESOURCES)) {
		const entry = value as Stored | null
		if (!entry || typeof entry.updatedAt !== 'number') {
			stale.push(key)
			continue
		}
		if (entry.updatedAt < cutoff) {
			stale.push(key)
			continue
		}
		hydrated.set(key, entry.data)
		written.set(key, JSON.stringify(entry.data))
	}
	for (const key of stale) await remove(RESOURCES, key)
}

const run = async (userId: string | null) => {
	await openDb()
	available = true

	const meta = Meta.safeParse(await readOne(META, 'cache'))
	boundUserId =
		meta.success && meta.data.version === CACHE_VERSION
			? meta.data.userId
			: null

	// A first run, a bumped cache version, or a cache left by another account.
	if (boundUserId === null || (userId !== null && userId !== boundUserId)) {
		await reset(userId)
		return
	}

	// No session snapshot to check the stores against — leave them alone and
	// let /auth/me decide, rather than paint another account's content.
	if (userId === null) return

	await load()
}

// Fill `hydrated` before the first paint. Never rejects: a browser with
// IndexedDB blocked or broken just gets a cold start.
export const hydratePersisted = async (userId: string | null) => {
	// Claim the incoming user up front. On the timeout path `run` may not have
	// reached the meta check yet, and AuthProvider's first bind would
	// otherwise read this as a change of account and throw the cache away.
	boundUserId = userId
	try {
		await Promise.race([
			run(userId),
			new Promise((_, reject) =>
				setTimeout(
					() => reject(new Error('IndexedDB open timed out')),
					OPEN_TIMEOUT_MS,
				),
			),
		])
	} catch {
		available = false
		hydrated.clear()
		written.clear()
	}
}

// Consumed once: the hook that owns the key parses it and holds the result in
// memory from then on.
export const takeHydrated = (key: string): unknown => {
	const value = hydrated.get(key)
	hydrated.delete(key)
	return value
}

export const persistEntry = (key: string, value: unknown) => {
	if (!available || boundUserId === null) return
	const json = JSON.stringify(value)
	if (written.get(key) === json) return
	written.set(key, json)
	const entry: Stored = { data: value, updatedAt: Date.now() }
	void write(RESOURCES, key, entry).catch(() => {
		// Forget the serialisation so the next write retries.
		written.delete(key)
	})
}

export const forgetEntry = (key: string) => {
	hydrated.delete(key)
	written.delete(key)
	if (available) void remove(RESOURCES, key).catch(() => undefined)
}

export const bindPersistedToUser = (userId: string | null) => {
	void reset(userId).catch(() => undefined)
}
